import PageNav from "./components/PageNav";
import { useState } from 'react';
import { 
  PlayCircleIcon, 
  BookOpenIcon, 
  PhotoIcon,
  PencilSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon 
} from '@heroicons/react/20/solid';

export default function Tutorials() {
  const [expandedTip, setExpandedTip] = useState(null);

  const toggleTip = (id) => {
    setExpandedTip(expandedTip === id ? null : id);
  };

  const tutorials = [
    {
      id: 1,
      type: "Photo Stories",
      title: "How to Create Photo Stories",
      description: "Learn how to create personalized storybooks featuring your family photos",
      duration: "3 minutes",
      videoUrl: "https://static.mytalemaker.com/PhotoStoriesDescriptionMTM.mp4",
      thumbnail: "https://static.mytalemaker.com/thumbnail-photoStories.webp",
      icon: PhotoIcon
    },
 /*   {
      id: 2,
      type: "Avatar Stories",
      title: "How to Create Avatar Stories",
      description: "Step-by-step guide to creating stories with customizable avatar characters",
      duration: "3 minutes",
      videoUrl: "YOUR_VIDEO_URL_HERE",
      thumbnail: "https://static.mytalemaker.com/tutorial-avatar-thumb.jpg",
      icon: BookOpenIcon,
      comingSoon: true
    },
    {
      id: 3,
      type: "Editing",
      title: "How to Edit Your Story",
      description: "Learn to refine your story text and customize illustrations",
      duration: "3 minutes",
      videoUrl: "YOUR_VIDEO_URL_HERE",
      thumbnail: "https://static.mytalemaker.com/tutorial-edit-thumb.jpg",
      icon: PencilSquareIcon,
      comingSoon: true
    } */
  ];

  const photoTips = [
    {
      id: 1,
      title: "Photo Quality Requirements",
      content: "Upload clear, well-lit photos with at least 300x300 pixel resolution. Avoid blurry, dark, or low-resolution images for best results."
    },
    {
      id: 2,
      title: "Single Person Only",
      content: "Each photo should show only one person. Group photos or multiple people in the frame won't work properly with our AI."
    },
    {
      id: 3,
      title: "No Props or Toys",
      content: "Keep the focus on the person. Avoid toys, stuffed animals, or other distracting objects in the frame."
    },
    {
      id: 4,
      title: "Face Clearly Visible",
      content: "Make sure the face is clearly visible. Skip sunglasses, masks, or anything covering the face."
    },
    {
      id: 5,
      title: "Good Lighting & Background",
      content: "Use natural daylight when possible and prefer a neutral background. This helps our AI create better character illustrations."
    }
  ];

  return (
    <div>
      <PageNav className="absolute inset-x-0 top-0 z-50" />
      
      <div className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Tutorials & Help
            </h1>
            <p className="mt-4 text-lg text-gray-600">
              Learn how to create amazing personalized storybooks with step-by-step video guides
            </p>
          </div>

          {/* Dynamic Grid - Centers when 1 item, Grid when multiple */}
          <div className={`mt-16 ${tutorials.length === 1 ? 'flex justify-center' : 'grid gap-8 sm:grid-cols-2 lg:grid-cols-3'}`}>
            {tutorials.length === 1 ? (
              <div className="w-full max-w-md">
                {tutorials.map((tutorial) => (
                  <div 
                    key={tutorial.id}
                    className="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="relative aspect-video w-full overflow-hidden bg-gray-200">
                      {tutorial.comingSoon ? (
                        <div className="flex h-full items-center justify-center">
                          <div className="text-center">
                            <tutorial.icon className="mx-auto h-16 w-16 text-gray-400" />
                            <p className="mt-2 text-sm font-medium text-gray-500">Coming Soon</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <img 
                            src={tutorial.thumbnail} 
                            alt={tutorial.title}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <PlayCircleIcon className="h-16 w-16 text-white" />
                          </div>
                        </>
                      )}
                      
                      {!tutorial.comingSoon && (
                        <div className="absolute bottom-2 right-2 rounded bg-black/75 px-2 py-1 text-xs font-medium text-white">
                          {tutorial.duration}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-center gap-2">
                        <tutorial.icon className="h-5 w-5 text-indigo-600" />
                        <span className="text-xs font-medium text-indigo-600">{tutorial.type}</span>
                      </div>
                      
                      <h3 className="mt-2 text-lg font-semibold text-gray-900">
                        {tutorial.title}
                      </h3>
                      
                      <p className="mt-2 flex-1 text-sm text-gray-600">
                        {tutorial.description}
                      </p>

                      {!tutorial.comingSoon && (
                        <button
                          onClick={() => window.open(tutorial.videoUrl, '_blank')}
                          className="mt-4 w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors duration-200"
                        >
                          Watch Tutorial
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              tutorials.map((tutorial) => (
                <div 
                  key={tutorial.id}
                  className="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="relative aspect-video w-full overflow-hidden bg-gray-200">
                    {tutorial.comingSoon ? (
                      <div className="flex h-full items-center justify-center">
                        <div className="text-center">
                          <tutorial.icon className="mx-auto h-16 w-16 text-gray-400" />
                          <p className="mt-2 text-sm font-medium text-gray-500">Coming Soon</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <img 
                          src={tutorial.thumbnail} 
                          alt={tutorial.title}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <PlayCircleIcon className="h-16 w-16 text-white" />
                        </div>
                      </>
                    )}
                    
                    {!tutorial.comingSoon && (
                      <div className="absolute bottom-2 right-2 rounded bg-black/75 px-2 py-1 text-xs font-medium text-white">
                        {tutorial.duration}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-2">
                      <tutorial.icon className="h-5 w-5 text-indigo-600" />
                      <span className="text-xs font-medium text-indigo-600">{tutorial.type}</span>
                    </div>
                    
                    <h3 className="mt-2 text-lg font-semibold text-gray-900">
                      {tutorial.title}
                    </h3>
                    
                    <p className="mt-2 flex-1 text-sm text-gray-600">
                      {tutorial.description}
                    </p>

                    {!tutorial.comingSoon && (
                      <button
                        onClick={() => window.open(tutorial.videoUrl, '_blank')}
                        className="mt-4 w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors duration-200"
                      >
                        Watch Tutorial
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

     {/*     <div className="mt-24">
            <div className="mx-auto max-w-3xl">
              <div className="text-center">
                <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                  Photo Quality Tips
                </h2>
                <p className="mt-4 text-base text-gray-600">
                  Follow these guidelines for best results when uploading character photos
                </p>
              </div>

              <div className="mt-8 space-y-3">
                {photoTips.map((tip) => (
                  <div
                    key={tip.id}
                    className={`bg-white rounded-lg border ${
                      expandedTip === tip.id ? 'border-indigo-200' : 'border-gray-200'
                    } shadow-sm cursor-pointer transition-all duration-200`}
                    onClick={() => toggleTip(tip.id)}
                  >
                    <div className="flex items-center justify-between p-4">
                      <h3 className="text-base font-semibold text-gray-900">
                        {tip.title}
                      </h3>
                      <span className="ml-6 flex-shrink-0">
                        {expandedTip === tip.id ? (
                          <ChevronUpIcon className="h-5 w-5 text-indigo-600" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-indigo-600" />
                        )}
                      </span>
                    </div>
                    
                    {expandedTip === tip.id && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-gray-600">
                          {tip.content}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
*/}
          <div className="mt-24 bg-indigo-50 rounded-2xl p-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-2xl font-bold text-gray-900">Need More Help?</h2>
              <p className="mt-4 text-base text-gray-600">
                Can't find what you're looking for? We're here to help!
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href='mailto:contact@mytalemaker.com'
                  className='inline-flex items-center justify-center rounded-md bg-indigo-600 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors duration-200'
                >
                  Contact Support
                </a>
                <a
                  href="/"
                  className="inline-flex items-center justify-center rounded-md bg-white px-6 py-3 text-sm font-semibold text-gray-900 border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
                >
                  Back to Home
                </a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}